// PDF request-form import. Lazy-loads pdfjs-dist on first use; reads the
// uploaded PDF in the browser, extracts the text layer AND the AcroForm
// annotation values (fillable forms - like the Gemeente Amsterdam
// "openbare stroomvoorzieningen" template - keep the typed values in form
// fields the text layer never shows), reconstructs reading order, and runs
// the label → value matcher in pdfImportParser.ts to pre-fill designer's
// add-request modal. Falls back to "admin types it manually" on failure.
//
// PDF.js worker URL points at the bundled worker (/node_modules path is
// rewritten by Vite at build time).

import { buildTextLayout, CHECKED, extractFields, type ExtractedFields, type TextEntry } from './pdfImportParser';

const importBtn = document.getElementById('import-pdf-btn');
if (importBtn) {
  // Build the modal lazily on first open so the rest of the admin page
  // stays light when nobody is importing.
  const modalHtml = `
    <div class="modal-overlay" id="pdf-import-modal" role="dialog" aria-modal="true" style="display:none;">
      <div class="modal" style="max-width:540px;">
        <button class="modal__close" type="button" id="pdf-import-close" aria-label="Close">×</button>
        <div class="modal__body">
          <div class="modal__title">Import PDF request form</div>
          <p style="color:var(--sk-muted);font-size:13.5px;margin-bottom:16px;">
            Drop a request-form PDF below. The fields the parser finds get pre-filled into the new-request modal - review before saving.
          </p>
          <div id="pdf-drop-area" style="border:2px dashed var(--sk-border,#ccc);padding:32px 16px;text-align:center;background:var(--sk-blue-100,#f5f7fb);cursor:pointer;border-radius:8px;">
            <div style="font-size:13.5px;color:var(--sk-navy-800,#0a2a5c);">Drag a PDF here or click to select</div>
            <input type="file" id="pdf-file-input" accept=".pdf,application/pdf" style="display:none;"/>
          </div>
          <div id="pdf-status" style="margin-top:14px;font-size:13px;display:none;"></div>
        </div>
        <div class="modal__foot">
          <button class="btn btn--ghost" type="button" id="pdf-import-cancel">Cancel</button>
        </div>
      </div>
    </div>`;
  document.body.insertAdjacentHTML('beforeend', modalHtml);

  const modal = document.getElementById('pdf-import-modal')!;
  const dropArea = document.getElementById('pdf-drop-area')!;
  const fileInput = document.getElementById('pdf-file-input') as HTMLInputElement;
  const status = document.getElementById('pdf-status')!;

  function openModal(): void {
    modal.classList.add('is-open');
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }
  function closeModal(): void {
    modal.classList.remove('is-open');
    modal.style.display = 'none';
    document.body.style.overflow = '';
    status.style.display = 'none';
  }

  importBtn.addEventListener('click', openModal);
  document.getElementById('pdf-import-close')?.addEventListener('click', closeModal);
  document.getElementById('pdf-import-cancel')?.addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });

  dropArea.addEventListener('click', () => fileInput.click());
  dropArea.addEventListener('dragover', (e) => { e.preventDefault(); dropArea.style.background = 'var(--sk-white,#fff)'; });
  dropArea.addEventListener('dragleave', () => { dropArea.style.background = 'var(--sk-blue-100,#f5f7fb)'; });
  dropArea.addEventListener('drop', (e) => {
    e.preventDefault();
    dropArea.style.background = 'var(--sk-blue-100,#f5f7fb)';
    const file = e.dataTransfer?.files[0];
    if (file) void handleFile(file);
  });
  fileInput.addEventListener('change', (e) => {
    const file = (e.currentTarget as HTMLInputElement).files?.[0];
    if (file) void handleFile(file);
  });

  importBtn.removeAttribute('hidden');

  function setStatus(text: string, type: 'info' | 'ok' | 'error' = 'info'): void {
    status.style.display = 'block';
    status.textContent = text;
    status.style.color = type === 'error' ? '#b6433a' : type === 'ok' ? '#1aa863' : 'var(--sk-muted,#666)';
  }

  async function handleFile(file: File): Promise<void> {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setStatus('Only PDF files are supported.', 'error');
      return;
    }
    setStatus('Loading PDF.js…', 'info');
    try {
      const pdfjs = await import('pdfjs-dist');
      // Vite bundles the worker URL - point pdfjs at it.
      pdfjs.GlobalWorkerOptions.workerSrc = (await import('pdfjs-dist/build/pdf.worker.mjs?url')).default;

      setStatus('Parsing PDF…', 'info');
      const buffer = await file.arrayBuffer();
      const doc = await pdfjs.getDocument({ data: buffer }).promise;
      const allText: string[] = [];
      for (let i = 1; i <= doc.numPages; i++) {
        const page = await doc.getPage(i);
        const [content, annotations] = await Promise.all([
          page.getTextContent(),
          page.getAnnotations(),
        ]);
        const entries: TextEntry[] = content.items
          .filter((it) => 'str' in it)
          .map((it) => ({ x: it.transform[4], y: it.transform[5], text: it.str }));

        // Merge AcroForm values at their widget positions so the
        // reconstructed lines read "label: value" just like printed text.
        // Checked radios/checkboxes become a [X] marker the parser keys on.
        for (const a of annotations as Array<Record<string, unknown>>) {
          if (a.subtype !== 'Widget') continue;
          const rect = a.rect as number[] | undefined;
          if (!rect || rect.length < 4) continue;
          const x = rect[0];
          const y = (rect[1] + rect[3]) / 2;
          if ((a.fieldType === 'Tx' || a.fieldType === 'Ch') && typeof a.fieldValue === 'string' && a.fieldValue.trim()) {
            entries.push({ x, y, text: a.fieldValue.trim() });
          } else if (a.fieldType === 'Btn' && a.fieldValue && a.fieldValue !== 'Off') {
            // Radio groups share one fieldValue; only the widget whose own
            // export value matches is the selected one. Plain checkboxes
            // have no buttonValue - any non-Off fieldValue means checked.
            const isSelected = a.radioButton ? a.buttonValue === a.fieldValue : true;
            if (isSelected) entries.push({ x, y, text: CHECKED });
          }
        }
        allText.push(buildTextLayout(entries));
      }
      const fullText = allText.join('\n');
      const fields = extractFields(fullText);
      // The municipal form has no project field - the file name (e.g.
      // "Stroom aanvraag - Baarsjescoop 2026.pdf") is the best stand-in.
      if (!fields.project_name) {
        fields.project_name = file.name.replace(/\.pdf$/i, '').trim();
      }
      const found = Object.keys(fields).filter((k) => fields[k as keyof ExtractedFields]).length;
      setStatus(`PDF read - ${found} fields detected. Opening add-request…`, 'ok');
      setTimeout(() => {
        closeModal();
        openAddRequestWith(fields, file.name);
      }, 600);
    } catch (err) {
      console.error('[pdf-import]', err);
      setStatus(`Could not read PDF: ${err instanceof Error ? err.message : err}`, 'error');
    }
  }

  function openAddRequestWith(fields: ExtractedFields, sourceFile: string): void {
    const btn = document.getElementById('add-request-btn');
    btn?.click();

    setTimeout(() => {
      const form = document.getElementById('add-request-form') as HTMLFormElement | null;
      if (!form) return;
      const setIfExists = (name: string, value: string | undefined): void => {
        if (!value) return;
        const el = form.elements.namedItem(name) as HTMLInputElement | null;
        if (el) el.value = value;
      };
      setIfExists('organisation', fields.organisation);
      setIfExists('contact_name', fields.contact_name);
      setIfExists('contact_email', fields.contact_email);
      setIfExists('contact_phone', fields.contact_phone);
      setIfExists('aansluiter_name', fields.aansluiter_name);
      setIfExists('aansluiter_email', fields.aansluiter_email);
      setIfExists('aansluiter_phone', fields.aansluiter_phone);
      setIfExists('project_name', fields.project_name);
      setIfExists('start_at', fields.start_at);
      setIfExists('end_at', fields.end_at);
      setIfExists('max_current_a', fields.max_current_a);

      // Multi-station: drive each ID through the admin dropdown's change
      // handler, which is the one path that converts a pick into a chip
      // + updates the in-memory list. The dropdown is loaded async on
      // modal open, so we poll for a populated option list before adding.
      if (fields.station_ids && fields.station_ids.length > 0) {
        void addStationsViaPicker(fields.station_ids);
      }

      // Stash the source filename + any context the form has no field for
      // (connection type, permit, water wish) in the internal note.
      const noteEl = form.elements.namedItem('note') as HTMLTextAreaElement | null;
      if (noteEl) {
        const parts = [
          ...(fields.notes ?? []),
          `(Imported from PDF: ${sourceFile})`,
        ];
        noteEl.value = (noteEl.value ? noteEl.value + '\n\n' : '') + parts.join('\n');
      }
    }, 200);
  }

  /**
   * Add stations to the admin add-request modal by their human-readable id
   * (e.g. "PS-1234"). Polls for the dropdown's option list to be populated
   * because <c>ensurePointsLoaded</c> runs async on modal open. IDs that
   * don't resolve to any option are silently skipped.
   */
  async function addStationsViaPicker(ids: string[]): Promise<void> {
    const sel = document.getElementById('add-point') as HTMLSelectElement | null;
    if (!sel) return;
    // Wait up to ~3s for the dropdown to populate. The points-loaded check
    // is "has at least one non-placeholder option" - empty list means the
    // /api/map/power-stations call hasn't returned yet.
    const start = Date.now();
    while (sel.options.length <= 1 && Date.now() - start < 3000) {
      await new Promise((r) => setTimeout(r, 100));
    }
    for (const id of ids) {
      const opt = Array.from(sel.options).find((o) => o.dataset.psId?.toLowerCase() === id.toLowerCase());
      if (!opt) continue;
      sel.value = opt.value;
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}
